import React, { useEffect, useState } from "react";
import { Save, Eye, Sparkles, Music, Utensils, Images, Star, ShoppingBag, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite, useSection } from "@/context/SiteContext";

const TOGGLES = [
  { key: "lunch", label: "Dagens lunch", desc: "Visa dagens lunch-sektion", Icon: Utensils },
  { key: "hours_section", label: "Öppettider", desc: "Visa öppettider-sektionen på hemsidan", Icon: Clock },
  { key: "daily_special", label: "Dagens förslag", desc: "En handplockad eller slumpad maträtt från menyn", Icon: Sparkles },
  { key: "events", label: "Kommande aktiviteter", desc: "Trubadurer, konserter och andra händelser", Icon: Music },
  { key: "gallery_teaser", label: "Galleri-teaser", desc: '"Se mer från oss"-sektionen', Icon: Images },
  { key: "reviews", label: "Recensioner", desc: "Kundomdömen och betyg", Icon: Star },
  { key: "order_section", label: "Beställ-sektionen", desc: "Beställ mat / hämta ordning", Icon: ShoppingBag },
];

export default function FeatureToggles() {
  const { reload } = useSite();
  const features = useSection("features");
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (features && Object.keys(features).length > 0) {
      setFlags({ ...features });
    }
  }, [features]);

  const toggle = (key) => {
    setFlags((f) => ({ ...f, [key]: !f[key] }));
    setDirty(true);
  };

  const save = async () => {
    if (!flags) return;
    setSaving(true);
    try {
      await api.put("/site", { features: flags });
      toast.success("Funktioner uppdaterade");
      setDirty(false);
      if (reload) await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  if (!flags) return <div className="py-8 text-center text-[var(--text-muted)]">Laddar…</div>;

  return (
    <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden" data-testid="feature-toggles">
      <div className="px-6 py-5 border-b border-[var(--border)]">
        <h3 className="font-serif-display text-2xl font-bold flex items-center gap-2">
          <Eye size={20} /> Funktioner på hemsidan
        </h3>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Stäng av sektioner du inte vill visa just nu — innehållet raderas inte, det bara döljs och kan slås på när som helst.
        </p>
      </div>
      <ul className="divide-y divide-[var(--border)]">
        {TOGGLES.map(({ key, label, desc, Icon }) => {
          const on = !!flags[key];
          return (
            <li key={key} className="px-6 py-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full grid place-items-center border ${
                on ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-stone-50 border-stone-200 text-stone-400"
              }`}>
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold flex items-center gap-2">
                  {label}
                  {!on && <span className="text-xs font-normal text-stone-500 uppercase tracking-wider">Dold</span>}
                </div>
                <div className="text-sm text-[var(--text-muted)]">{desc}</div>
              </div>
              <button
                onClick={() => toggle(key)}
                data-testid={`feature-toggle-${key}`}
                aria-pressed={on}
                className={`relative w-14 h-8 rounded-full transition-colors ${
                  on ? "bg-emerald-600" : "bg-stone-300"
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                    on ? "translate-x-6" : ""
                  }`}
                />
                <span className="sr-only">{on ? "Aktiv" : "Avstängd"}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex justify-end p-4 bg-[var(--surface-alt)] border-t border-[var(--border)]">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="feature-toggles-save"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-5 py-2.5 font-semibold disabled:opacity-40"
        >
          <Save size={15} /> {saving ? "Sparar…" : dirty ? "Spara ändringar" : "Sparat"}
        </button>
      </div>
    </div>
  );
}
